import React, { useEffect, useRef, useState } from "react";
import styles from "./AssessmentStepTransition.module.scss";

export interface AssessmentStepTransitionProps {
  stepKey: string;
  children: React.ReactNode;
}

// Mirrors mobile's step transition (Assessment/index.tsx): fade out the
// current step over 80ms, swap content, then fade the next step in over
// 240ms — both eased with the same ease-in-out curve.
const FADE_OUT_MS = 80;

const AssessmentStepTransition: React.FC<AssessmentStepTransitionProps> = ({
  stepKey,
  children,
}) => {
  const [displayKey, setDisplayKey] = useState(stepKey);
  const [isVisible, setIsVisible] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Last children rendered for the current displayKey — kept fresh below
  // while stepKey === displayKey, and left untouched (still showing the old
  // step) during the brief fade-out window after stepKey changes.
  const lastChildrenRef = useRef(children);
  if (stepKey === displayKey) {
    lastChildrenRef.current = children;
  }

  useEffect(() => {
    if (stepKey === displayKey) return;

    setIsVisible(false);
    timeoutRef.current = setTimeout(() => {
      setDisplayKey(stepKey);
      setIsVisible(true);
    }, FADE_OUT_MS);

    return () => clearTimeout(timeoutRef.current);
  }, [stepKey, displayKey]);

  return (
    // The data attributes let e2e tell the step on screen apart from the
    // outgoing one that lingers through the fade-out.
    <div
      className={isVisible ? styles["fade-in"] : styles["fade-out"]}
      data-testid="assessment-step"
      data-step-key={displayKey}
      data-step-state={isVisible ? "shown" : "leaving"}
    >
      {lastChildrenRef.current}
    </div>
  );
};

export default AssessmentStepTransition;
